import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  Show,
  For,
} from "solid-js";
import { useNavigate } from "@solidjs/router";
import {
  DragDropProvider,
  DragDropSensors,
  DragOverlay,
  SortableProvider,
  closestCenter,
} from "@thisbeyond/solid-dnd";
import type { DragEvent } from "@thisbeyond/solid-dnd";
import { EmptyState, Button, Dropdown, DropdownItem } from "../components/ui";
import { ImportCsvModal } from "../components/work-items/ImportCsvModal";
import { ImportGithubModal } from "../components/work-items/ImportGithubModal";
import {
  AutoPrioritizeButton,
  AutoPrioritizePreviewBar,
  AutoPrioritizeProgressBar,
  BacklogRow,
  BacklogSkeleton,
  BulkActionBar,
  FilterBar,
  FocusConfirmBar,
} from "../components/backlog";
import type { BacklogFilters } from "../components/backlog";
import { Tooltip, useTooltips } from "../components/Tooltip";
import { useApiWithSource, useWebSocket, useToast } from "../hooks";
import { useFirstVisit } from "../utils/useFirstVisit";
import { api, bulkChangeStatus } from "../api";
import {
  autoPrioritize,
  bulkReorder,
  detectAutoPrioritizeFocus,
} from "../api/backlog";
import { surfaceQuotaExhausted } from "../utils/aiError";
import type {
  AutoPrioritizeRunProgress,
  BoardEvent,
  FocusDetection,
  RelevanceSummary,
  QuotaScope,
  WorkItem,
  WorkItemListResponse,
  WorkItemStatus,
  WorkItemType,
} from "../api";
import { useProjectId } from "../contexts/ProjectContext";
import { useSetChatViewContext } from "../contexts/ChatViewContext";

/** Column caption styling shared by every header cell. */
const CAPTION = "font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 ";

/**
 * Select-all header row with three-state checkbox (unchecked / indeterminate / checked).
 * The caption cells mirror BacklogRow's columns so the two line up.
 */
const SelectAllHeader: Component<{
  selectedCount: number;
  totalCount: number;
  onToggle: () => void;
}> = (props) => {
  let checkboxRef: HTMLInputElement | undefined;

  createEffect(() => {
    if (checkboxRef) {
      checkboxRef.indeterminate =
        props.selectedCount > 0 && props.selectedCount < props.totalCount;
    }
  });

  const label = () => {
    if (props.selectedCount === 0) return "Select all";
    if (props.selectedCount === props.totalCount)
      return `All ${props.totalCount} selected`;
    return `${props.selectedCount} of ${props.totalCount} selected`;
  };

  return (
    <div
      data-table-header
      class="flex items-center h-8 px-6 bg-paper border-b border-line"
    >
      {/* Spacer for drag handle column */}
      <div class="w-6 shrink-0 mr-1" />
      <div class="shrink-0 mr-2" data-no-row-click>
        <label class="flex h-6.5 w-6.5 items-center justify-center cursor-pointer">
          <input
            ref={checkboxRef}
            type="checkbox"
            class="w-3.5 h-3.5 rounded-sm border-line-strong accent-ink focus-ring cursor-pointer"
            checked={
              props.selectedCount > 0 &&
              props.selectedCount === props.totalCount
            }
            onClick={props.onToggle}
            aria-label={label()}
          />
        </label>
      </div>
      {/* Spacer for the type icon column */}
      <span class="w-[15px] shrink-0 mr-1.5" />
      <span class={CAPTION + "shrink-0 w-16 mr-2"}>ID</span>
      <span class={CAPTION + "flex-1 min-w-0 mr-3"}>Title</span>
      <Show when={props.selectedCount > 0}>
        <span class="font-mono text-2xs tabular-nums text-ink-2 shrink-0 mr-3">
          {props.selectedCount}/{props.totalCount}
        </span>
      </Show>
      <span
        class={CAPTION + "hidden xl:inline-block shrink-0 w-14 text-right mr-2"}
      >
        Est
      </span>
      <span class={CAPTION + "hidden md:inline-block shrink-0 w-5 mr-2"}>
        Pri
      </span>
      <span class={CAPTION + "shrink-0 w-28"}>Status</span>
    </div>
  );
};

/**
 * BacklogPage - Flat list view of all non-done work items, ordered by backlog position.
 *
 * Renders each item as a compact BacklogRow with type icon, status badge,
 * priority indicator, and parent breadcrumb. Includes a filter bar with
 * URL-synced type, status, and search filters.
 *
 * Supports drag-and-drop reordering when no filters are active.
 */
export const BacklogPage: Component = () => {
  const navigate = useNavigate();
  const projectId = useProjectId();

  const tooltips = useTooltips();
  const isFirstVisit = useFirstVisit("backlog");
  const [filterBarRef, setFilterBarRef] = createSignal<HTMLElement | null>(
    null,
  );
  const [bulkActionBarRef, setBulkActionBarRef] =
    createSignal<HTMLElement | null>(null);
  const [showFilterTooltip, setShowFilterTooltip] = createSignal(false);
  const [showBulkActionTooltip, setShowBulkActionTooltip] = createSignal(false);

  const [selectedIds, setSelectedIds] = createSignal<Set<string>>(new Set());
  const [lastClickedIndex, setLastClickedIndex] = createSignal<number | null>(
    null,
  );

  const [filters, setFilters] = createSignal<BacklogFilters>({
    typeIds: [],
    statusIds: [],
    search: "",
  });

  // Signal to trigger filter clear from empty state CTA
  const [clearFilterSignal, setClearFilterSignal] = createSignal(0);
  const handleClearFilters = () => setClearFilterSignal((n) => n + 1);

  const [showImportModal, setShowImportModal] = createSignal(false);
  const [showGithubImportModal, setShowGithubImportModal] = createSignal(false);

  const [activeItem, setActiveItem] = createSignal<WorkItem | null>(null);

  // Auto-prioritize state machine
  type AutoPrioritizeState =
    | "idle"
    | "detecting"
    | "confirming"
    | "loading"
    | "previewing"
    | "applying";
  const [autoPrioritizeState, setAutoPrioritizeState] =
    createSignal<AutoPrioritizeState>("idle");
  // Detection awaiting the user's confirmation (FLOW-920), and the focus the
  // previewed proposal was actually computed for.
  const [focusDetection, setFocusDetection] =
    createSignal<FocusDetection | null>(null);
  const [activeFocus, setActiveFocus] = createSignal<string | null>(null);
  // Which pass of the run is in flight (FLOW-946). `focus_detection` is set
  // by this page — the backend cannot report it, because detection finishes
  // before there is a job to poll.
  const [runProgress, setRunProgress] =
    createSignal<AutoPrioritizeRunProgress | null>(null);
  const [originalOrderSnapshot, setOriginalOrderSnapshot] = createSignal<
    WorkItem[] | null
  >(null);
  const [proposedReasons, setProposedReasons] = createSignal<
    Record<string, string>
  >({});
  // The previewed order came from the quota fallback tier (FLOW-794), and the
  // ceiling that bound it there when the backend named one (FLOW-930)
  const [autoPrioritizeDegraded, setAutoPrioritizeDegraded] =
    createSignal(false);
  // What the relevance filter kept for the previewed proposal (FLOW-921)
  const [autoPrioritizeRelevance, setAutoPrioritizeRelevance] =
    createSignal<RelevanceSummary | null>(null);
  const [autoPrioritizeDegradedScope, setAutoPrioritizeDegradedScope] =
    createSignal<QuotaScope | undefined>();
  // Auto-prioritization can now run for minutes (FLOW-886 polling); guard
  // against mutating state after the component is gone or the user has
  // switched to a different project mid-poll.
  let disposed = false;
  onCleanup(() => {
    disposed = true;
  });
  // Bumped on every click and on every project change — the run a poll was
  // started under is compared against the current value (`stale()` in
  // `handleAutoPrioritize`) rather than re-deriving "am I still current"
  // from `projectId()` alone. A live `projectId() !== pid` check un-stops
  // itself on A→B→A: switching back to the original project during an
  // abandoned poll makes that poll look current again, so it can resume,
  // land a proposal, and race a second click's fresh poll for the same
  // read-once backend result. The counter only ever goes up, so a run that
  // was ever superseded stays stale even if the project comes back around.
  let autoPrioritizeRun = 0;
  // The page survives a project switch (useApiWithSource re-fetches rather
  // than remounting), but an in-flight poll belongs to the OLD project.
  // Without this, the new project's button inherits a stuck "loading" state
  // — disabled, showing "Analyzing…" — for as long as the abandoned poll
  // keeps running (up to 180s), and every click is swallowed by
  // `if (autoPrioritizeState() !== "idle") return;`. `defer: true` so this
  // doesn't fire on mount, only on an actual switch.
  createEffect(
    on(
      projectId,
      () => {
        autoPrioritizeRun++;
        setAutoPrioritizeState("idle");
        setOriginalOrderSnapshot(null);
        setProposedReasons({});
        setAutoPrioritizeDegraded(false);
        setAutoPrioritizeDegradedScope(undefined);
        setFocusDetection(null);
        setActiveFocus(null);
        setRunProgress(null);
      },
      { defer: true },
    ),
  );

  // Fetch work items sorted by backlog position, excluding done items
  const {
    data: response,
    loading,
    error,
    refetch,
    mutate: mutateResponse,
  } = useApiWithSource(projectId, (pid) =>
    api.get<WorkItemListResponse>(
      `/projects/${pid}/work-items?exclude_done=true&sort_by=backlog_position&limit=500`,
    ),
  );

  // Lightweight fetch to detect whether the project has ANY items (including done)
  // Used to distinguish "empty project" from "all items completed"
  const { data: totalResponse } = useApiWithSource(projectId, (pid) =>
    api.get<WorkItemListResponse>(`/projects/${pid}/work-items?limit=1`),
  );
  // Default to true while loading to avoid flash of "empty project" state
  const projectHasItems = createMemo(() => {
    const resp = totalResponse();
    if (resp === undefined) return true;
    return resp.count > 0;
  });

  const { data: statuses } = useApiWithSource(projectId, (pid) =>
    api.get<WorkItemStatus[]>(`/projects/${pid}/statuses`),
  );

  const { data: itemTypes } = useApiWithSource(projectId, (pid) =>
    api.get<WorkItemType[]>(`/projects/${pid}/work-items/types`),
  );

  const items = createMemo(() => response()?.items ?? []);

  // Combine itemTypes and statuses for FilterBar (avoids non-null assertions)
  const filterBarData = createMemo(() => {
    const types = itemTypes();
    const sts = statuses();
    if (types && sts) return { types, statuses: sts };
    return undefined;
  });

  const filteredItems = createMemo(() => {
    let result = items();
    const f = filters();

    if (f.typeIds.length > 0) {
      result = result.filter((item) => f.typeIds.includes(item.item_type_id));
    }
    if (f.statusIds.length > 0) {
      result = result.filter((item) => f.statusIds.includes(item.status_id));
    }
    if (f.search) {
      const q = f.search.toLowerCase();
      result = result.filter((item) => item.title.toLowerCase().includes(q));
    }
    return result;
  });

  const isFiltered = createMemo(() => {
    const f = filters();
    return (
      f.typeIds.length > 0 || f.statusIds.length > 0 || f.search.length > 0
    );
  });

  /** Map of item ID -> delta (positive = moved up, negative = moved down). Empty when not previewing. */
  const movementMap = createMemo<Map<string, number>>(() => {
    if (autoPrioritizeState() !== "previewing") return new Map();
    const snapshot = originalOrderSnapshot();
    if (!snapshot) return new Map();
    const original = new Map<string, number>();
    snapshot.forEach((it, idx) => original.set(it.id, idx));
    const map = new Map<string, number>();
    items().forEach((it, idx) => {
      const o = original.get(it.id);
      if (o !== undefined) map.set(it.id, o - idx);
    });
    return map;
  });

  const parentTitles = createMemo(() => {
    const titles: Record<string, string> = {};
    for (const item of items()) {
      titles[item.id] = item.title;
    }
    return titles;
  });

  const getItemType = (typeId: string) =>
    itemTypes()?.find((t) => t.id === typeId);

  const handleSelect = (id: string, selected: boolean, event: MouseEvent) => {
    const currentItems = filteredItems();
    const currentIndex = currentItems.findIndex((i) => i.id === id);

    const lastIdx = lastClickedIndex();
    if (event.shiftKey && lastIdx !== null && selected) {
      // Shift+click: select range between last clicked and current
      const last = lastIdx;
      const from = Math.min(last, currentIndex);
      const to = Math.max(last, currentIndex);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (let i = from; i <= to; i++) {
          next.add(currentItems[i].id);
        }
        return next;
      });
      // Don't update anchor on shift+click
    } else {
      // Normal click: toggle single item
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
      setLastClickedIndex(currentIndex);
    }
  };

  const handleSelectAll = () => {
    const visible = filteredItems();
    if (selectedIds().size === visible.length) {
      setSelectedIds(new Set<string>());
    } else {
      setSelectedIds(new Set<string>(visible.map((i) => i.id)));
    }
    setLastClickedIndex(null);
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set<string>());
    setLastClickedIndex(null);
  };

  const { showError, showSuccess } = useToast();

  const handleBulkStatusChange = async (statusId: string) => {
    const ids = [...selectedIds()];
    if (ids.length === 0) return;

    const targetStatus = statuses()?.find((s) => s.id === statusId);
    const statusName = targetStatus?.name ?? "new status";
    const count = ids.length;

    const snapshot = response();

    // Optimistic update: update status_id on selected items, or remove if done
    const isDone =
      targetStatus?.category === "done" ||
      targetStatus?.category === "cancelled";
    mutateResponse((prev) => {
      if (!prev) return prev;
      const updatedItems = isDone
        ? prev.items.filter((item) => !selectedIds().has(item.id))
        : prev.items.map((item) =>
            selectedIds().has(item.id)
              ? { ...item, status_id: statusId }
              : item,
          );
      return { items: updatedItems, count: updatedItems.length };
    });

    // Clear selection and return focus to the first visible row
    handleDeselectAll();
    requestAnimationFrame(() => {
      scrollRef?.querySelector<HTMLElement>('[role="row"]')?.focus();
    });

    try {
      await bulkChangeStatus(projectId(), {
        item_ids: ids,
        status_id: statusId,
      });
      showSuccess(
        `Moved ${count} item${count > 1 ? "s" : ""} to ${statusName}`,
      );
    } catch (e) {
      if (snapshot) mutateResponse(snapshot);
      setSelectedIds(new Set<string>(ids));
      showError(
        `Failed to update: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    }
  };

  // Prune selection when filtered items change (filter change may hide selected items)
  createEffect(() => {
    const visibleIds = new Set(filteredItems().map((i) => i.id));
    setSelectedIds((prev) => {
      const pruned = new Set([...prev].filter((id) => visibleIds.has(id)));
      return pruned.size !== prev.size ? pruned : prev;
    });
    setLastClickedIndex(null);
  });

  const setViewContext = useSetChatViewContext();

  createEffect(() => {
    setViewContext({
      type: "backlog",
      filters: {
        typeIds: filters().typeIds,
        statusIds: filters().statusIds,
        search: filters().search,
      },
      visibleItemIds: filteredItems().map((i) => i.id),
      selectedItemIds: [...selectedIds()],
      totalItemCount: items().length,
    });
  });

  onCleanup(() => {
    setViewContext({ type: "general" });
  });

  const isDoneStatus = (statusId: string): boolean => {
    const s = statuses()?.find((st) => st.id === statusId);
    return s?.category === "done" || s?.category === "cancelled";
  };

  const handleWebSocketMessage = (event: BoardEvent) => {
    if (!("project_id" in event.payload)) return;
    if (event.payload.project_id !== projectId()) return;

    switch (event.type) {
      case "WorkItemCreated": {
        const item = event.payload.item;
        if (isDoneStatus(item.status_id)) break;
        const current = items();
        if (current.some((i) => i.id === item.id)) break;
        // Insert at bottom with position after the last item
        const maxPos =
          current.length > 0
            ? Math.max(...current.map((i) => i.backlog_position ?? 0))
            : 0;
        const newItem = {
          ...item,
          backlog_position: maxPos + 1000,
        };
        mutateResponse((prev) =>
          prev
            ? { items: [...prev.items, newItem], count: prev.count + 1 }
            : { items: [newItem], count: 1 },
        );
        break;
      }
      case "WorkItemUpdated": {
        const item = event.payload.item;
        const current = items();
        const exists = current.some((i) => i.id === item.id);
        if (exists) {
          if (isDoneStatus(item.status_id)) {
            mutateResponse((prev) =>
              prev
                ? {
                    items: prev.items.filter((i) => i.id !== item.id),
                    count: prev.count - 1,
                  }
                : prev,
            );
          } else {
            mutateResponse((prev) =>
              prev
                ? {
                    items: prev.items.map((i) => (i.id === item.id ? item : i)),
                    count: prev.count,
                  }
                : prev,
            );
          }
        }
        break;
      }
      case "WorkItemStatusChanged": {
        const { item_id, item } = event.payload;
        const current = items();
        const exists = current.some((i) => i.id === item_id);
        const done = isDoneStatus(item.status_id);

        if (exists && done) {
          mutateResponse((prev) =>
            prev
              ? {
                  items: prev.items.filter((i) => i.id !== item_id),
                  count: prev.count - 1,
                }
              : prev,
          );
        } else if (exists && !done) {
          mutateResponse((prev) =>
            prev
              ? {
                  items: prev.items.map((i) => (i.id === item_id ? item : i)),
                  count: prev.count,
                }
              : prev,
          );
        } else if (!exists && !done) {
          // Not in list but moved back to active — add it
          const maxPos =
            current.length > 0
              ? Math.max(...current.map((i) => i.backlog_position ?? 0))
              : 0;
          mutateResponse((prev) =>
            prev
              ? {
                  items: [
                    ...prev.items,
                    { ...item, backlog_position: maxPos + 1000 },
                  ],
                  count: prev.count + 1,
                }
              : {
                  items: [{ ...item, backlog_position: maxPos + 1000 }],
                  count: 1,
                },
          );
        }
        break;
      }
      case "WorkItemDeleted": {
        const deletedId = event.payload.item_id;
        mutateResponse((prev) =>
          prev
            ? {
                items: prev.items.filter((i) => i.id !== deletedId),
                count: prev.count - 1,
              }
            : prev,
        );
        // Also remove from selection
        setSelectedIds((prev) => {
          if (!prev.has(deletedId)) return prev;
          const next = new Set(prev);
          next.delete(deletedId);
          return next;
        });
        break;
      }
    }
  };

  const { status: wsStatus } = useWebSocket({
    onMessage: handleWebSocketMessage,
    onError: () =>
      showError("Real-time connection error. Updates may be delayed."),
  });

  // Tooltip setup - show filter tooltip on first visit
  createEffect(() => {
    if (
      isFirstVisit() &&
      !tooltips.isDismissed("backlog-epic-filter") &&
      filterBarData() &&
      !loading()
    ) {
      // Wait for FilterBar to render, then find the Type filter multiselect
      const timer = setTimeout(() => {
        // The FilterBar renders a multiselect with placeholder "Type"
        const typeFilter = Array.from(document.querySelectorAll("button")).find(
          (btn) => btn.textContent?.includes("Type"),
        );
        if (typeFilter) {
          setFilterBarRef(typeFilter);
          setShowFilterTooltip(true);
        }
      }, 500);
      onCleanup(() => clearTimeout(timer));
    }
  });

  // Show bulk action tooltip when items are selected for the first time
  createEffect(() => {
    if (
      isFirstVisit() &&
      !tooltips.isDismissed("backlog-bulk-actions") &&
      selectedIds().size > 0
    ) {
      // Wait for BulkActionBar to render
      const timer = setTimeout(() => {
        const bulkBar = document.querySelector(
          '[class*="fixed bottom-0"]',
        ) as HTMLElement;
        if (bulkBar) {
          setBulkActionBarRef(bulkBar);
          setShowBulkActionTooltip(true);
        }
      }, 300);
      onCleanup(() => clearTimeout(timer));
    }
  });

  const handleDismissFilterTooltip = () => {
    tooltips.dismiss("backlog-epic-filter");
    setShowFilterTooltip(false);
  };

  const handleDismissBulkActionTooltip = () => {
    tooltips.dismiss("backlog-bulk-actions");
    setShowBulkActionTooltip(false);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && selectedIds().size > 0) {
      handleDeselectAll();
    }
  };
  if (typeof document !== "undefined") {
    document.addEventListener("keydown", handleKeyDown);
    onCleanup(() => document.removeEventListener("keydown", handleKeyDown));
  }

  // Item count display: "12 of 47 items" when filtered, "47 items" otherwise
  const itemCountText = createMemo(() => {
    const total = items().length;
    const filtered = filteredItems().length;
    if (isFiltered() && filtered !== total) {
      return `${filtered} of ${total} items`;
    }
    return `${total} items`;
  });

  // Drag is disabled when filters are active, items are selected, or auto-prioritize is active
  const dragEnabled = createMemo(
    () =>
      !isFiltered() &&
      selectedIds().size === 0 &&
      autoPrioritizeState() === "idle",
  );

  // IDs for SortableProvider - only from the displayed (filtered) items
  const sortableIds = createMemo(() => filteredItems().map((i) => i.id));

  /**
   * Handle drag start - store active item for overlay
   */
  const handleDragStart = (event: DragEvent) => {
    const itemId = event.draggable.id as string;
    const item = items().find((i) => i.id === itemId);
    if (item) setActiveItem(item);
  };

  /**
   * Handle drag end - reorder items optimistically, then persist via API.
   *
   * Uses the backlog reorder endpoint which takes { item_id, after_id }.
   * after_id = null means move to top of list.
   */
  const handleDragEnd = async (event: DragEvent) => {
    setActiveItem(null);

    const { draggable, droppable } = event;
    if (!draggable || !droppable) return;

    const dragId = draggable.id as string;
    const dropId = droppable.id as string;
    if (dragId === dropId) return;

    const currentItems = items();
    const fromIndex = currentItems.findIndex((i) => i.id === dragId);
    const toIndex = currentItems.findIndex((i) => i.id === dropId);
    if (fromIndex < 0 || toIndex < 0) return;

    const newOrder = [...currentItems];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);

    // Determine after_id: the item directly above the moved item in new order
    const movedNewIndex = newOrder.findIndex((i) => i.id === dragId);
    const after_id = movedNewIndex > 0 ? newOrder[movedNewIndex - 1].id : null;

    const snapshot = response();

    mutateResponse({ items: newOrder, count: newOrder.length });

    try {
      await api.post(`/projects/${projectId()}/backlog/reorder`, {
        item_id: dragId,
        after_id,
      });
    } catch (e) {
      if (snapshot) mutateResponse(snapshot);
      showError(
        `Failed to reorder: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    }
  };

  const prioritizationFailed = (e: unknown) => {
    if (!surfaceQuotaExhausted(e))
      showError(
        `Could not compute prioritization: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    setAutoPrioritizeState("idle");
  };

  /** Run auto-prioritize for a focus: call AI, capture snapshot, render proposed order. */
  const runAutoPrioritize = async (focus: string | null) => {
    const pid = projectId();
    // Latched against this specific run, not re-derived from projectId()
    // alone: a live `projectId() !== pid` check un-stops itself if the user
    // switches away and back to `pid` inside one poll interval, letting an
    // abandoned poller resume and race a later click's fresh one for the
    // same read-once backend result (FLOW-886 fix-round-3).
    const run = ++autoPrioritizeRun;
    const stale = () =>
      disposed || run !== autoPrioritizeRun || projectId() !== pid;
    // State first, then the detection: clearing it while the state is still
    // "confirming" leaves FocusConfirmBar mounted with a null candidate, so
    // its live region re-renders with the "no focus could be inferred" copy
    // on its way out.
    setAutoPrioritizeState("loading");
    setRunProgress({ stage: "preparing" });
    setFocusDetection(null);
    try {
      const proposal = await autoPrioritize(pid, focus, stale, (p) => {
        if (!stale()) setRunProgress(p);
      });
      // The poll can run for minutes: bail out without touching state if
      // the page has been torn down, this run has been superseded by a
      // later click, or the user has since switched projects.
      if (stale()) return;

      const itemMap = new Map(items().map((i) => [i.id, i] as const));
      const reorderedItems = proposal.proposed_order
        .map((id) => itemMap.get(id))
        .filter((i): i is WorkItem => i !== undefined);

      if (reorderedItems.length !== items().length) {
        showError("Backlog changed while computing — please retry.");
        setAutoPrioritizeState("idle");
        return;
      }

      setOriginalOrderSnapshot(items());
      setProposedReasons(proposal.item_reasons);
      setAutoPrioritizeDegraded(proposal.degraded);
      // Like `activeFocus` below: a joined run was filtered against whatever
      // focus started it, so its counts must not be labelled as this click's.
      setAutoPrioritizeRelevance(
        proposal.joined ? null : (proposal.relevance ?? null),
      );
      setAutoPrioritizeDegradedScope(proposal.degradedScope);
      // A run that joined one already in flight produced an order for that
      // run's focus, which need not be this one — label it with nothing
      // rather than with a focus it did not use (FLOW-920).
      setActiveFocus(proposal.joined ? null : focus);
      mutateResponse({ items: reorderedItems, count: reorderedItems.length });
      setAutoPrioritizeState("previewing");
      showSuccess("Prioritization ready — review the proposed order.");
    } catch (e) {
      if (stale()) return;
      prioritizationFailed(e);
    }
  };

  /**
   * Trigger auto-prioritize: detect the project's current focus first
   * (FLOW-920). A detection the backend can't vouch for pauses here for the
   * user to confirm or edit; a confident one runs straight away.
   */
  const handleAutoPrioritize = async () => {
    if (autoPrioritizeState() !== "idle") return;
    const pid = projectId();
    // Same latch as the run below — the detection await is short, but a
    // project switch during it must not land a bar belonging to the old
    // project (see the comment in `runAutoPrioritize`).
    const run = ++autoPrioritizeRun;
    const stale = () =>
      disposed || run !== autoPrioritizeRun || projectId() !== pid;
    setAutoPrioritizeState("detecting");
    setRunProgress({ stage: "focus_detection" });
    try {
      const detection = await detectAutoPrioritizeFocus(pid);
      if (stale()) return;
      if (detection.needs_confirmation) {
        setFocusDetection(detection);
        setAutoPrioritizeState("confirming");
        return;
      }
      await runAutoPrioritize(detection.focus?.summary ?? null);
    } catch (e) {
      if (stale()) return;
      prioritizationFailed(e);
    }
  };

  /** Confirm bar accepted: run with the text the user settled on. */
  const handleConfirmFocus = (focus: string) => {
    if (autoPrioritizeState() !== "confirming") return;
    void runAutoPrioritize(focus);
  };

  /** Confirm bar dismissed: nothing runs. */
  const handleCancelFocus = () => {
    if (autoPrioritizeState() !== "confirming") return;
    setFocusDetection(null);
    setAutoPrioritizeState("idle");
  };

  /** Apply preview: POST /bulk-reorder. */
  const handleApplyPriority = async () => {
    if (autoPrioritizeState() !== "previewing") return;
    setAutoPrioritizeState("applying");
    try {
      await bulkReorder(projectId(), {
        ordered_item_ids: items().map((i) => i.id),
      });
      setOriginalOrderSnapshot(null);
      setProposedReasons({});
      setActiveFocus(null);
      setAutoPrioritizeState("idle");
      showSuccess("Backlog reordered.");
    } catch (e) {
      showError(
        `Failed to apply: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
      setAutoPrioritizeState("previewing");
    }
  };

  /** Cancel preview: restore snapshot. */
  const handleCancelPriority = () => {
    if (autoPrioritizeState() !== "previewing") return;
    const snap = originalOrderSnapshot();
    if (snap) {
      mutateResponse({ items: snap, count: snap.length });
    }
    setOriginalOrderSnapshot(null);
    setProposedReasons({});
    setAutoPrioritizeDegraded(false);
    setAutoPrioritizeDegradedScope(undefined);
    setActiveFocus(null);
    setAutoPrioritizeState("idle");
  };

  // Scroll container ref for keyboard navigation and scroll position preservation
  let scrollRef: HTMLDivElement | undefined;
  const setScrollRef = (el: HTMLDivElement) => {
    scrollRef = el;
    // Restore scroll position when returning from item detail
    const saved = sessionStorage.getItem(`backlog-scroll-${projectId()}`);
    if (saved) {
      requestAnimationFrame(() => {
        el.scrollTop = parseInt(saved, 10);
      });
      sessionStorage.removeItem(`backlog-scroll-${projectId()}`);
    }
  };

  // Save scroll position before navigating to item detail
  const handleRowClick = (item: { id: string }) => {
    if (scrollRef) {
      sessionStorage.setItem(
        `backlog-scroll-${projectId()}`,
        String(scrollRef.scrollTop),
      );
    }
    navigate(`/items/${item.id}?from=backlog`);
  };

  // Arrow key navigation between rows in the grid
  const handleGridKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    if (!scrollRef) return;

    const rows = Array.from(
      scrollRef.querySelectorAll<HTMLElement>('[role="row"]'),
    );
    if (rows.length === 0) return;

    const currentIndex = rows.findIndex((r) => r === document.activeElement);
    let nextIndex: number;

    if (e.key === "ArrowDown") {
      nextIndex = currentIndex < rows.length - 1 ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : rows.length - 1;
    }

    e.preventDefault();
    rows[nextIndex].focus();
  };

  return (
    <div class="h-full flex flex-col bg-paper">
      {/* Header */}
      <div class="flex items-center justify-between h-11 px-6 border-b border-line bg-surface">
        <div class="flex items-center gap-3">
          <h1 class="text-xl font-semibold text-ink">Backlog</h1>
          <Show when={response()}>
            <span class="font-mono text-sm tabular-nums text-ink-3">
              {itemCountText()}
            </span>
          </Show>
        </div>
        <div class="flex items-center gap-3">
          {/* WebSocket connection status indicator */}
          <div
            class="flex items-center gap-1.5 text-xs"
            title={`Real-time updates: ${wsStatus()}`}
          >
            <div
              class={`w-2 h-2 rounded-full ${
                wsStatus() === "connected"
                  ? "bg-signal-green"
                  : wsStatus() === "connecting"
                    ? "bg-signal-amber animate-pulse"
                    : "bg-line-strong"
              }`}
            />
            <span class="text-ink-3 hidden sm:inline">
              {wsStatus() === "connected"
                ? "Live"
                : wsStatus() === "connecting"
                  ? "Connecting..."
                  : "Offline"}
            </span>
          </div>
          <Dropdown
            trigger={
              <Button variant="secondary" size="sm">
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                Import
                <svg
                  class="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </Button>
            }
          >
            <DropdownItem onClick={() => setShowImportModal(true)}>
              <div class="flex items-center gap-2">
                <svg
                  class="w-4 h-4 text-ink-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Import from CSV
              </div>
            </DropdownItem>
            <DropdownItem onClick={() => setShowGithubImportModal(true)}>
              <div class="flex items-center gap-2">
                <svg
                  class="w-4 h-4 text-ink-3"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 0c4.42 0 8 3.58 8 8a8.013 8.013 0 0 1-5.45 7.59c-.4.08-.55-.17-.55-.38 0-.27.01-1.13.01-2.2 0-.75-.25-1.23-.54-1.48 1.78-.2 3.65-.88 3.65-3.95 0-.88-.31-1.59-.82-2.15.08-.2.36-1.02-.08-2.12 0 0-.67-.22-2.2.82-.64-.18-1.32-.27-2-.27-.68 0-1.36.09-2 .27-1.53-1.03-2.2-.82-2.2-.82-.44 1.1-.16 1.92-.08 2.12-.51.56-.82 1.28-.82 2.15 0 3.06 1.86 3.75 3.64 3.95-.23.2-.44.55-.51 1.07-.46.21-1.61.55-2.33-.66-.15-.24-.6-.83-1.23-.82-.67.01-.27.38.01.53.34.19.73.9.82 1.13.16.45.68 1.31 2.69.94 0 .67.01 1.3.01 1.49 0 .21-.15.45-.55.38A7.995 7.995 0 0 1 0 8c0-4.42 3.58-8 8-8Z" />
                </svg>
                Import from GitHub
              </div>
            </DropdownItem>
          </Dropdown>
          <AutoPrioritizeButton
            onClick={handleAutoPrioritize}
            disabled={isFiltered() || items().length < 2}
            loading={
              autoPrioritizeState() === "detecting" ||
              autoPrioritizeState() === "loading"
            }
            disabledReason={
              isFiltered()
                ? "Clear filters to auto-prioritize"
                : "Backlog needs at least 2 items"
            }
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={loading()}
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <Show when={filterBarData()} keyed>
        {(data) => (
          <FilterBar
            itemTypes={data.types}
            statuses={data.statuses}
            onFilterChange={setFilters}
            clearSignal={clearFilterSignal()}
          />
        )}
      </Show>

      {/* Info banner when drag is disabled due to filters */}
      <Show when={isFiltered() && filteredItems().length > 0}>
        <div class="px-6 py-1.5 bg-signal-amber-tint border-b border-signal-amber/25 text-xs text-signal-amber flex items-center gap-1.5">
          <svg
            class="w-3.5 h-3.5 shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clip-rule="evenodd"
            />
          </svg>
          Drag reordering is disabled while filters are active.
        </div>
      </Show>

      {/* Select-all header row */}
      <Show when={filteredItems().length > 0}>
        <SelectAllHeader
          selectedCount={selectedIds().size}
          totalCount={filteredItems().length}
          onToggle={handleSelectAll}
        />
      </Show>

      {/* Stage progress — the same slot as the focus and preview bars, and
          mutually exclusive with both: `confirming` sits between the two
          states this covers (FLOW-946). */}
      <Show
        when={
          autoPrioritizeState() === "detecting" ||
          autoPrioritizeState() === "loading"
        }
      >
        <AutoPrioritizeProgressBar
          stage={runProgress()?.stage ?? "preparing"}
          progress={runProgress()?.progress}
        />
      </Show>

      {/* Focus confirmation bar — same slot as the preview bar (FLOW-920) */}
      <Show when={autoPrioritizeState() === "confirming"}>
        <FocusConfirmBar
          candidate={focusDetection()?.focus?.summary ?? null}
          rationale={focusDetection()?.focus?.rationale ?? null}
          busy={false}
          onConfirm={handleConfirmFocus}
          onCancel={handleCancelFocus}
        />
      </Show>

      {/* Auto-prioritize preview bar */}
      <Show
        when={
          autoPrioritizeState() === "previewing" ||
          autoPrioritizeState() === "applying"
        }
      >
        <AutoPrioritizePreviewBar
          movedCount={
            Array.from(movementMap().values()).filter((d) => d !== 0).length
          }
          applying={autoPrioritizeState() === "applying"}
          onApply={handleApplyPriority}
          onCancel={handleCancelPriority}
          degraded={autoPrioritizeDegraded()}
          degradedScope={autoPrioritizeDegradedScope()}
          focus={activeFocus()}
          relevance={autoPrioritizeRelevance()}
          onRegenerate={() => {
            handleCancelPriority();
            void handleAutoPrioritize();
          }}
        />
      </Show>

      {/* Content */}
      <div class="flex-1 overflow-hidden">
        <Show when={!loading()} fallback={<BacklogSkeleton />}>
          <Show
            when={!error()}
            fallback={
              <div class="h-full flex items-center justify-center p-8">
                <EmptyState
                  icon={
                    <svg
                      class="w-12 h-12"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.5"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  }
                  title="Error loading backlog"
                  description={
                    error()?.message ?? "An unexpected error occurred"
                  }
                  action={
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => refetch()}
                    >
                      Try again
                    </Button>
                  }
                />
              </div>
            }
          >
            <Show
              when={filteredItems().length > 0}
              fallback={
                <div class="h-full flex items-center justify-center p-8">
                  <Show
                    when={isFiltered()}
                    fallback={
                      <Show
                        when={!projectHasItems()}
                        fallback={
                          /* All items completed (items exist but all are done/excluded) */
                          <EmptyState
                            icon={
                              <svg
                                class="w-12 h-12 text-signal-green"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="1.5"
                                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            }
                            title="All caught up!"
                            description="Every item in your backlog has been completed. Nice work."
                            action={
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate("/board")}
                              >
                                Go to Board
                              </Button>
                            }
                          />
                        }
                      >
                        {/* No items in project at all */}
                        <EmptyState
                          icon={
                            <svg
                              class="w-12 h-12"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.5"
                                d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.125 1.125 0 010 2.25H5.625a1.125 1.125 0 010-2.25z"
                              />
                            </svg>
                          }
                          title="Your backlog is empty"
                          description="Create work items from the board to start building your backlog."
                          action={
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => navigate("/board")}
                            >
                              Go to Board
                            </Button>
                          }
                        />
                      </Show>
                    }
                  >
                    {/* No items match current filters */}
                    <EmptyState
                      icon={
                        <svg
                          class="w-12 h-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="1.5"
                            d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                          />
                        </svg>
                      }
                      title="No items match your filters"
                      description="Try adjusting or removing some filters to see more items."
                      action={
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleClearFilters}
                        >
                          Clear all filters
                        </Button>
                      }
                    />
                  </Show>
                </div>
              }
            >
              {/* Scrollable backlog list with drag-and-drop */}
              <DragDropProvider
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                collisionDetector={closestCenter}
              >
                <DragDropSensors />
                <div
                  ref={setScrollRef}
                  class="h-full overflow-y-auto"
                  role="grid"
                  aria-label="Backlog items"
                  aria-rowcount={filteredItems().length}
                  onKeyDown={handleGridKeyDown}
                >
                  <SortableProvider ids={sortableIds()}>
                    <For each={filteredItems()}>
                      {(item) => (
                        <BacklogRow
                          item={item}
                          itemType={getItemType(item.item_type_id)}
                          statuses={statuses() ?? []}
                          parentTitle={
                            item.parent_id
                              ? parentTitles()[item.parent_id]
                              : undefined
                          }
                          selected={selectedIds().has(item.id)}
                          dragEnabled={dragEnabled()}
                          onSelect={handleSelect}
                          onClick={handleRowClick}
                          movement={
                            autoPrioritizeState() === "previewing"
                              ? movementMap().get(item.id)
                              : undefined
                          }
                          reason={proposedReasons()[item.id]}
                        />
                      )}
                    </For>
                  </SortableProvider>
                </div>
                <DragOverlay>
                  <Show when={activeItem()}>
                    {(item) => (
                      <div class="opacity-85">
                        <BacklogRow
                          item={item()}
                          itemType={getItemType(item().item_type_id)}
                          statuses={[]}
                          selected={false}
                          dragEnabled={false}
                          overlay
                          onSelect={() => {}}
                          onClick={() => {}}
                        />
                      </div>
                    )}
                  </Show>
                </DragOverlay>
              </DragDropProvider>
            </Show>
          </Show>
        </Show>
      </div>

      {/* Bulk action bar */}
      <Show when={selectedIds().size > 0}>
        <BulkActionBar
          selectedCount={selectedIds().size}
          statuses={statuses() ?? []}
          onChangeStatus={handleBulkStatusChange}
          onDeselectAll={handleDeselectAll}
        />
      </Show>

      {/* Feature Discovery Tooltips */}
      <Tooltip
        anchor={filterBarRef()}
        content="Filter your backlog by epic to focus on one workstream at a time."
        id="backlog-epic-filter"
        onDismiss={handleDismissFilterTooltip}
        show={showFilterTooltip()}
      />
      <Tooltip
        anchor={bulkActionBarRef()}
        content="Select multiple items to bulk-update status, assignee, or priority."
        id="backlog-bulk-actions"
        onDismiss={handleDismissBulkActionTooltip}
        show={showBulkActionTooltip()}
      />

      {/* CSV Import Modal */}
      <ImportCsvModal
        open={showImportModal()}
        onClose={() => setShowImportModal(false)}
        projectId={projectId()}
        onImportComplete={() => refetch()}
      />

      {/* GitHub Issues Import Modal */}
      <ImportGithubModal
        open={showGithubImportModal()}
        onClose={() => setShowGithubImportModal(false)}
        projectId={projectId()}
        onImportComplete={() => refetch()}
      />
    </div>
  );
};

export default BacklogPage;
